import { useState } from 'react'
import { dayIn, hereZone, midnightIsoIn, zoneOfCity } from '../lib/zone'
import { addDays, clockText, factsTable, startOf } from '../lib/deskBrowse'
import { useAddItemForEntry, useAddListForEntry, useRemoveCommitment, useRemoveItem, useScheduleEntry } from '../lib/mutations'
import { useCommitments, useListItems, useLists, useVenues } from '../lib/queries'
import { attempt, useWriteError } from '../lib/useAction'
import { idsOf, signalFeatures } from '../lib/entries'
import { rowTitle, runCount, setPlace } from '../lib/rowMeta'
import { paneShowings, placesOf } from '../lib/places'
import { placeAddress, sheetPlace } from '../lib/sheetPlace'
import { listsForEvent } from '../lib/listKinds'
import type { ShowingLite } from '../components/CardBody'
import { useToast } from '../ui'
import { toOriginal } from './BrowseRow'
import { useAskAbout } from './AskPanel'
import { toastAddedToList, toastCommitted } from './undo'
import type { FeedRow } from '../lib/types'

const dateLabel = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })

export interface BrowsePaneProps {
  row: FeedRow
  kind: string
  today: string
  now: Date
  pic: string | null
  description: string | null
  descriptionUrl: string | null
  showings: ShowingLite[]
  saved: boolean
  hidden: boolean
  onSave: (row: FeedRow) => void
  onHide: (row: FeedRow) => void
  onClose: () => void
}

/** The open row, in the same family at the same sizes as the list. */
export function BrowsePane({
  row, kind, today, now, pic, description, descriptionUrl, showings, saved, hidden, onSave, onHide, onClose,
}: BrowsePaneProps) {
  const askAbout = useAskAbout()
  const schedule = useScheduleEntry()
  const unschedule = useRemoveCommitment()
  const commitmentsQ = useCommitments()
  const venuesById = useVenues().data?.byId
  const [listOpen, setListOpen] = useState(false)
  const [allShowings, setAllShowings] = useState(false)
  const own = startOf(row)
  // C2.1: the row's own showings are read in its town's zone.
  const rowTz = zoneOfCity(row.city)
  const [date, setDate] = useState(() => (own && own.day >= today ? own.day : today))

  const ids = idsOf(row)
  const committed = new Set(
    (commitmentsQ.data ?? []).filter(c => c.entry_id && ids.includes(c.entry_id) && c.starts_at)
      .map(c => new Date(c.starts_at!).getTime()),
  )
  // UI-audit a3-07: a set's place is the list row's — `setPlace`, "11
  // cinemas" — never its canonical's venue, which is one member's (VATERLAND
  // is filed at Il Kino). When says how many nights and to when, as the
  // phone card does; the list below says which, under each cinema.
  const setWhere = setPlace(row)
  const set = setWhere ? { where: setWhere, when: runCount(row, { now, showings }) } : null
  const askText = [row.title, setWhere ?? row.venue, own ? dateLabel(own.day) : null].filter(Boolean).join(' · ')
  const facts = factsTable(row, kind, set)
  const upcoming = showings
    .filter(s => s.kind !== 'run' && s.kind !== 'annotation' && dayIn(s.starts_at, rowTz) >= today)
  // One line per night *and place* (`uniqueShowings`): keyed on the instant
  // alone, two cinemas starting a set's film at the same minute (The Odyssey,
  // Sat 11:00, b-ware and Hackesche Höfe) were one line, and the second
  // cinema vanished. Grouped by cinema where there are several (a3-07), and
  // all of them on request (a3-08).
  const view = paneShowings(upcoming, placesOf(row), { all: allShowings, fallbackName: row.building ?? row.venue ?? '' })
  // Each night's own place, from the function the phone sheet reads
  // (`sheetPlace`): a night at another cinema gets that cinema's address and
  // its own ticket page, never the row's Il Kino link.
  const placeOf = (s: ShowingLite) => sheetPlace(row, s, id => venuesById?.get(id))

  const commit = (startsAt: string) => {
    schedule.mutate(
      { entryId: row.id, startsAt, title: row.title, features: signalFeatures(row) },
      { onSuccess: res => toastCommitted('Added to the Year', res, unschedule.mutate) },
    )
  }

  return (
    <aside className="br-pane" aria-label={row.title}>
      <div className="top">
        <h2>{row.title}</h2>
        <button type="button" className="b quiet" onClick={onClose}>Close</button>
      </div>
      {pic && <img className={`hero${kind === 'film' ? ' poster' : ''}`} src={pic} alt="" onError={toOriginal(row)} />}
      <div className="actions">
        <button type="button" className={`b${saved ? ' on' : ''}`} aria-pressed={saved} onClick={() => onSave(row)}>{saved ? 'Saved' : 'Save'}</button>
        <button type="button" className={`b${hidden ? ' on' : ''}`} aria-pressed={hidden} onClick={() => onHide(row)}>{hidden ? 'Hidden' : 'Not for me'}</button>
        <button type="button" className="b" aria-expanded={listOpen} onClick={() => setListOpen(o => !o)}>Add to list</button>
        {row.url && <a className="b" href={row.url} target="_blank" rel="noopener noreferrer">Open source ↗</a>}
        <button type="button" className="b" onClick={e => {
          askAbout(askText)
          // Where the desk is too narrow for list, pane and Ask side by side,
          // the pane is laid over the Ask column (browse.css, a4-01): the CSS
          // decides that, and the answer would arrive under the pane.
          const pane = e.currentTarget.closest('.br-pane')
          if (pane && getComputedStyle(pane).position === 'fixed') onClose()
        }}>Ask about this</button>
      </div>
      {listOpen && <ListPicker row={row} onDone={() => setListOpen(false)} />}
      {description && (
        <p className="desc">
          {description}
          {descriptionUrl && <> <a className="src" href={descriptionUrl} target="_blank" rel="noopener noreferrer">source ↗</a></>}
        </p>
      )}
      <dl className="facts">
        {facts.map(([k, v]) => <div key={k} className="f"><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      <section>
        <h3>Commit to a date</h3>
        {view.total > 0 ? (
          <div className="showings">
            {view.groups.map(g => {
              const at = view.grouped ? placeOf(g.showings[0]) : null
              const address = at ? placeAddress({ ...at, venue: null }) : ''
              return (
                <div className="shg" key={g.key} role={view.grouped ? 'group' : undefined} aria-label={view.grouped ? g.name : undefined}>
                  {view.grouped && (
                    <div className="place"><b>{g.name}</b>{address && <span>{address}</span>}</div>
                  )}
                  {g.showings.map(s => {
                    const done = committed.has(new Date(s.starts_at).getTime())
                    // tix-1: a cinema with no ticket page of its own gets the
                    // listing's film page, named — `via` — as the phone does.
                    const at = view.grouped ? placeOf(s) : null
                    const ticket = at?.booking ?? null
                    return (
                      <div className="sh" key={`${s.starts_at}|${s.venue_id ?? s.venue ?? ''}`}>
                        <span className="when">{[dateLabel(dayIn(s.starts_at, rowTz)), clockText(s.starts_at, rowTz)].filter(Boolean).join(' ')}</span>
                        <span className="where">
                          {view.grouped
                            ? ticket && <a href={ticket} target="_blank" rel="noopener noreferrer">{at?.via ? `Tickets via ${at.via} ↗` : 'Tickets ↗'}</a>
                            : s.venue ?? ''}
                        </span>
                        <button type="button" className="b small" disabled={done || schedule.isPending} onClick={() => commit(s.starts_at)}>
                          {done ? 'In the Year' : 'Commit'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            })}
            {view.more > 0 && (
              <button type="button" className="more" onClick={() => setAllShowings(true)}>
                Show {view.more} more showings
              </button>
            )}
          </div>
        ) : (
          <div className="datefield">
            <input type="date" value={date} min={today} max={addDays(today, 730)} onChange={e => setDate(e.target.value)} aria-label="Date" />
            <button type="button" className="b" disabled={!date || schedule.isPending} onClick={() => commit(midnightIsoIn(date, hereZone()))}>
              Commit on this day
            </button>
          </div>
        )}
        {schedule.isError && <div className="err" role="alert">{schedule.error.message}</div>}
      </section>
    </aside>
  )
}

/** The lists this row can go on, and a way to start one. A row already on a
 *  list says so and is not added twice. */
function ListPicker({ row, onDone }: { row: FeedRow; onDone: () => void }) {
  const toast = useToast()
  const listsQ = useLists()
  const itemsQ = useListItems()
  const add = useAddItemForEntry()
  const remove = useRemoveItem()
  const addList = useAddListForEntry()
  // The picker closes and says "Added" only once the insert lands; a failed
  // one stays open and says why, here where it was tried (a4-06).
  const error = useWriteError(add, addList)
  const [name, setName] = useState('')
  // a2-23: the lists an event fits, in the Lists index's order — the same
  // function as the phone's Add to list.
  const lists = listsForEvent(listsQ.data ?? [])
  const items = itemsQ.data ?? []
  const has = (listId: string) => items.some(i => i.list_id === listId && i.entry_id === row.id)
  const body = rowTitle(row)

  return (
    <div className="listpick">
      {lists.map(l => (
        <button type="button" key={l.id} className="opt" disabled={has(l.id)}
          onClick={async () => {
            if (await attempt(add.mutateAsync({ listId: l.id, entryId: row.id, body }))) { toastAddedToList(l.name, l.id, row.id, remove.mutate); onDone() }
          }}>
          <span>{l.name}</span><span className="c">{has(l.id) ? 'On it' : ''}</span>
        </button>
      ))}
      <form className="new" onSubmit={async e => {
        e.preventDefault()
        const n = name.trim()
        if (!n) return
        if (await attempt(addList.mutateAsync({ name: n, kind: 'collection', entryId: row.id, body }))) {
          toast({ text: `Added to ${n}` })
          onDone()
        }
      }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="New list" aria-label="New list name" />
        <button type="submit" className="b small" disabled={!name.trim() || addList.isPending}>Add</button>
      </form>
      {error && <div className="err" role="alert">{error}</div>}
    </div>
  )
}
